import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useStoreId } from '@thd-nucleus/experience-context';
import { useSearch, useRelatedSearchNav, QueryProvider } from '@thd-nucleus/data-sources';
import { RelatedSearchLinks } from './subcomponents/RelatedSearchLinks';
import { RelatedSearchProducts } from './subcomponents/RelatedSearchProducts';
import { RecentSearch } from './subcomponents/RecentSearch'
import './related-search.scss';

const RelatedSearch = (props) => (
  <QueryProvider dataSource="relatedSearch">
    {/* eslint-disable-next-line */}
    <RelatedSearchChild {...props} />
  </QueryProvider>
);

// @todo this should really be 2 components, one for search and one for product.
const RelatedSearchChild = (props) => {
  const {
    itemId,
    keyword,
    navParam,
    recent,
    storeId: defaultStoreId,
  } = props;

  const {
    searchData: searchDataProps,
    searchError: searchErrorProps,
    searchLoading: searchLoadingProps
  } = props;

  let searchData;
  let searchError;
  let searchLoading;

  if (recent) {
    return <RecentSearch />
  }

  const storeId = defaultStoreId || useStoreId();
  const [loadedStoreId, setLoadedStoreId] = useState(storeId);

  const hasPropData = !!(searchDataProps || searchErrorProps || typeof searchLoadingProps !== 'undefined');
  ({
    data: searchData,
    error: searchError,
    loading: searchLoading
  } = useSearch({
    domains: ['relatedResults'],
    storeId,
    keyword,
    skip: !keyword || hasPropData,
    ssr: true
  }));

  if (hasPropData) {
    searchData = searchDataProps;
    searchError = searchErrorProps;
    searchLoading = searchLoadingProps;
  }

  const loadedStoreRef = useRef(storeId);
  const {
    data: navData,
    error: navError,
    loading: navLoading,
    variables,
  } = useRelatedSearchNav({
    domains: ['relatedSearch', 'products'],
    productDomains: ['mediaSlim', 'identifiers', 'details'],
    navParam,
    storeId,
    itemId,
    skip: !navParam && !itemId,
    ssr: true
  });

  if (!navLoading && navData && variables.storeId) {
    if (variables.storeId !== loadedStoreId) {
      loadedStoreRef.current = variables.storeId;
    }
  }

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('related-search.ready'); }, []);

  const searchDataIssue = ((searchError && !searchData) || !searchData || searchLoading) && (keyword);
  const navDataIssue = ((navError && !navData) || !navData || navLoading) && (navParam);

  const relatedSearchModel = searchData?.searchModel;
  const relatedNavModel = navData?.relatedSearchModel;
  const relatedSearchModelKeywords = !relatedSearchModel?.relatedResults?.relatedKeywords?.length ? null
    : relatedSearchModel?.relatedResults?.relatedKeywords;

  const relatedKeywords = relatedSearchModelKeywords || relatedNavModel?.relatedSearch;
  // no sure why there is a reference to relatedSearchModel?.relatedProducts
  // as it does not exist in the api Schema. I think this affects search pages
  const relatedProducts = relatedSearchModel?.relatedProducts || navData?.relatedSearchModel?.products || [];

  if ((searchDataIssue || navDataIssue) || (!relatedKeywords?.length && !relatedProducts?.length)) {
    return null;
  }
  return (
    <div className="related-search">
      {
        !!relatedKeywords?.length
        && <RelatedSearchLinks relatedLinks={relatedKeywords} />
      }
      {
        !!relatedProducts?.length
        && <RelatedSearchProducts relatedSearchProducts={relatedProducts} />
      }
    </div>
  );
};

export { RelatedSearch };

RelatedSearchChild.displayName = 'RelatedSearch';

RelatedSearchChild.propTypes = {
  itemId: PropTypes.string,
  keyword: PropTypes.string,
  navParam: PropTypes.string,
  storeId: PropTypes.string,
  searchData: PropTypes.shape({}), // needs to be defined and removed from lint ignore
  searchError: PropTypes.shape({}),
  searchLoading: PropTypes.bool,
  recent: PropTypes.bool
};

RelatedSearchChild.defaultProps = {
  itemId: null,
  keyword: null,
  navParam: null,
  storeId: null,
  searchData: undefined,
  searchError: undefined,
  searchLoading: undefined,
  recent: false
};
